import React, { useState, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import { Col } from 'react-bootstrap';
import { PackagesEnum } from '../../../../enum/PackageEnum';
import { Campaign } from '../../../../model/campaign/Campaign';
import { calculatePastMonths, hasAccessRights } from '../../../../shared/functions/Functions';
import { IAllStates } from '../../../../store/rootReducer';
import { ErrorToast } from '../../../../utils/toasters';
import { CampaignDashPackageReminder } from '../../../shared/packages/components/CampaignDashPackageReminder';
import { CampaignService } from '../service';
import { CampaignBookingCard } from './components/CampaignBookingCard';
import { CampaignManageList } from './components/CampaignManageList';
import { CampaignManagement } from './components/CampaignManagement';
import { CampaignMeasure } from './components/CampaignMeasure';
import { IParams } from '../../../../model/shared/IParams';
import CampaignMobileMenu from './components/CampaignMobileMenu';
import { shallowEqual, useDispatch, useSelector } from 'react-redux';
import { CampaignSupervisorCard } from './components/CampaignSupervisorCard';
import { CampaignBriefingDashCard } from './components/CampaignBriefingDashCard';
import { User } from '../../../../model/user/User';
import { CampaignTeamCard } from './components/CampaignTeamCard';
import { CampaignDashboardService } from './service';
import { ICampaignManagement } from './components/dto/ICampaignManagement';
import { CampaignBundleCard } from './components/CampaignBundleCard';
import { AccessRightEnum } from '../../../../enum/AccessRightEnum';
import { CampaignDiscoveryCard } from './components/CampaignDiscoveryCard';
import CardLayout from '../../../../shared/components/card/CardLayout';
import {
    CampaignDashboardSkeleton,
    DashStatsCardSkeleton,
    DashUsersSkeleton,
} from './components/CampaignDashboardSkeletons';
import { StoreKeeper } from '../../../../store/store';
import { loadingSlice } from '../../../../store/general/loadingSlice';

export default function CampaignDashboard() {
    StoreKeeper.store.dispatch(loadingSlice.actions.setIsLoading(false));
    const dispatch = useDispatch();
    const { id } = useParams<keyof IParams>();
    const { company, accessRights } = useSelector((state: IAllStates) => state.auth.user);
    const isAclLoaded = useSelector((state: IAllStates) => state.selectedCampaign?.isAclLoaded, shallowEqual);

    const [supervisors, setSupervisors] = useState<User[]>([]);
    const [managementInfo, setManagementInfo] = useState<ICampaignManagement>();
    const [updateManagement, setUpdateManagement] = useState(false);
    const [campaign, setCampaign] = useState<Campaign>();

    const reminderClosingTime = localStorage.getItem('reminderClosingTime');
    const isBasic = company?.package?.name === PackagesEnum.BASIC;
    const isReminderShow = isBasic && (calculatePastMonths(reminderClosingTime) >= 1 || !reminderClosingTime);
    const canCreateOption = accessRights.includes(AccessRightEnum.CAN_CREATE_OPTIONS);
    const isTeamCollaborationEnabled = company?.package?.teamCollaboration;

    const [isSupervisorsLoading, setIsSupervisorsLoading] = useState(true);
    const [isManagementLoading, setIsManagementLoading] = useState(true);

    useEffect(() => {
        CampaignService.getCampaign(+id!)
            .then(data => {
                setCampaign(data);
            })
            .catch(error => ErrorToast(error));
    }, [dispatch, id]);

    useEffect(() => {
        CampaignService.getSupervisors(+id!)
            .then(data => {
                setSupervisors(data);
            })
            .catch(error => ErrorToast(error))
            .finally(() => setIsSupervisorsLoading(false));
    }, [id]);

    useEffect(() => {
        CampaignDashboardService.getManagementData(id)
            .then(data => {
                setManagementInfo(data);
            })
            .catch(error => ErrorToast(error))
            .finally(() => setIsManagementLoading(false));
    }, [id, updateManagement]);

    useEffect(() => {
        const script = document.createElement('script');
        script.src = 'https://assets.calendly.com/assets/external/widget.js';
        script.async = true;
        document.body.appendChild(script);

        return () => {
            document.body.removeChild(script);
        };
    }, []);

    return isAclLoaded ? (
        <div className="row d-lg-flex d-block mb-5 mb-md-0 pb-5 pb-md-0">
            <CampaignMobileMenu campaign={campaign!} setCampaign={setCampaign} />
            {isReminderShow && (
                <Col className="py-md-2 mb-3">
                    <CampaignDashPackageReminder defaultShown={isReminderShow} />
                </Col>
            )}
            <Col xs={12} className="mb-3">
                <CampaignBriefingDashCard />
            </Col>
            <Col lg={7}>
                {isAclLoaded && hasAccessRights(AccessRightEnum.CAN_VIEW_DISCOVERY) && <CampaignDiscoveryCard />}
                {isManagementLoading ? (
                    <DashStatsCardSkeleton
                        stats={4}
                        withSubtitle
                        withButton
                        withImages
                        withProgress={!campaign?.isCharity}
                    />
                ) : (
                    !!managementInfo &&
                    (managementInfo.isClient ? (
                        <CampaignBundleCard />
                    ) : (
                        <CampaignBookingCard isCharity={campaign?.isCharity} />
                    ))
                )}
                <CampaignManageList />
            </Col>
            <Col lg={5}>
                {hasAccessRights(AccessRightEnum.CAN_VIEW_BOOKING) &&
                    (isSupervisorsLoading || !!supervisors.length) && (
                        <CardLayout classCard="mb-3 mt-3 mt-md-0">
                            {isSupervisorsLoading ? (
                                <DashUsersSkeleton />
                            ) : (
                                <CampaignSupervisorCard supervisors={supervisors} />
                            )}
                        </CardLayout>
                    )}

                <CardLayout classCard={`mb-3 mt-3 mt-md-0 ${!isTeamCollaborationEnabled ? 'bg-muted' : ''}`}>
                    {isManagementLoading ? (
                        <DashUsersSkeleton />
                    ) : (
                        <CampaignManagement managementInfo={managementInfo} setUpdateManagement={setUpdateManagement} />
                    )}
                </CardLayout>

                {isManagementLoading ? (
                    <CardLayout classCard="mb-3 mt-3 mt-md-0">
                        <DashUsersSkeleton footer="end" />
                    </CardLayout>
                ) : (
                    <>
                        {managementInfo?.isSupplier && !!managementInfo?.clientUsers?.length && (
                            <CardLayout classCard="mb-3 mt-3 mt-md-0">
                                <CampaignTeamCard
                                    setUpdateManagement={setUpdateManagement}
                                    managementInfo={managementInfo}
                                />
                            </CardLayout>
                        )}
                        {!managementInfo?.isSupplier && !!managementInfo?.assignedUsers?.length && (
                            <CardLayout classCard="mb-3 mt-3 mt-md-0">
                                <CampaignTeamCard
                                    setUpdateManagement={setUpdateManagement}
                                    managementInfo={managementInfo}
                                />
                            </CardLayout>
                        )}
                    </>
                )}

                {/* {canCreateOption && <CampaignBundleSupplierCard />} */}
                {hasAccessRights(AccessRightEnum.CAN_VIEW_MEASURE) && <CampaignMeasure />}
            </Col>
        </div>
    ) : (
        <CampaignDashboardSkeleton />
    );
}
